import { useState, useEffect, useCallback, useRef } from "react";
import { listRiders } from "../services/riders.service";

/**
 * useRiderOptions — searchable partner (rider) options for Ant Design Select.
 *
 * Riders run into the thousands, so the list is server-searched rather than
 * loaded whole: an initial page gives the dropdown something to show, and
 * typing re-queries. Debounced so a fast typist does not fire a request per
 * keystroke.
 *
 * Usage:
 *   const { options, loading, onSearch } = useRiderOptions();
 *   <Select showSearch filterOption={false} onSearch={onSearch}
 *           options={options} loading={loading} />
 */
export default function useRiderOptions({ perPage = 50, status } = {}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchRiders = useCallback(
    async (search) => {
      // Tag each request so a slow earlier response cannot overwrite a newer one.
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const response = await listRiders({
          page: 1,
          per_page: perPage,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
        });
        if (requestId !== requestIdRef.current) return;

        // Paginated envelope is { success, data: { records, pagination } }; the
        // axios interceptor strips the outer layer, so records sit at data.records.
        const rows = response?.data?.records || [];
        setOptions(
          rows.map((r) => ({
            label: `${r.riderId || ""} — ${r.fullName || ""}`.replace(/^ — | — $/, ""),
            value: r._id,
            rider: r,
          }))
        );
      } catch (error) {
        // Left bare, a shape change here empties the dropdown with no clue why.
        console.error("Failed to load partner options", error);
        if (requestId === requestIdRef.current) setOptions([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [perPage, status]
  );

  useEffect(() => {
    fetchRiders();
    return () => clearTimeout(timerRef.current);
  }, [fetchRiders]);

  const onSearch = useCallback(
    (value) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fetchRiders(value), 300);
    },
    [fetchRiders]
  );

  return { options, loading, onSearch, refresh: fetchRiders };
}
